import {
  Box,
  Button,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  useMediaQuery,
} from "@mui/material";
import { useState, useRef, useEffect } from "react";
import { Upload, Trash2, X, FileText, XCircle } from "lucide-react";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';

const STEPS = [
  { key: "hotelInfo", labelKey: "StepHotelInfo" },
  { key: "licenseVerification", labelKey: "StepLicenseVerification" },
  { key: "roomInfo", labelKey: "StepRoomInfo" },
  { key: "additionalInfo", labelKey: "StepAdditionalInfo" },
  { key: "paymentMethod", labelKey: "StepPaymentMethod" },
];

const CURRENT_STEP_INDEX = 3;
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

const HotelAdd_RoomInfoAdd_2 = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width:950px)");

  const [requireBookingTerms, setRequireBookingTerms] = useState("");
  const [bookingTermsFile, setBookingTermsFile] = useState(null);
  const [bookingTermsPreviewUrl, setBookingTermsPreviewUrl] = useState(null);
  const [bookingTermsError, setBookingTermsError] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const bookingTermsInputRef = useRef(null);

  useEffect(() => {
    if (!bookingTermsFile) {
      setBookingTermsPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(bookingTermsFile);
    setBookingTermsPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [bookingTermsFile]);

  const handleFileSelect = (file) => {
    if (!file) return;
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setBookingTermsFile(null);
      setBookingTermsError(true);
      return;
    }
    setBookingTermsError(false);
    setBookingTermsFile(file);
  };

  const radioSx = {
    color: "#D1D1D1",
    "&.Mui-checked": { color: "#31BACD" },
  };

  const uploadBoxStyle = {
    display: "flex",
    alignItems: "center",
    gap: 2,
    backgroundColor: "#FFFFFF",
    border: "1.5px solid #D1D1D1",
    borderRadius: "10px",
    padding: isMobile ? "16px" : "20px 24px",
    cursor: "pointer",
    width: "100%",
    boxSizing: "border-box",
  };

  const fileCardStyle = {
    display: "flex",
    alignItems: "center",
    gap: 2,
    backgroundColor: "#FFFFFF",
    border: "1.5px solid #D1D1D1",
    borderRadius: "10px",
    padding: isMobile ? "16px" : "20px 24px",
    width: "100%",
    boxSizing: "border-box",
  };

  const isNextDisabled =
    !requireBookingTerms ||
    (requireBookingTerms === "yes" && (!bookingTermsFile || bookingTermsError));

  const pageStyle = {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "#F5F5F5",
  };

  const contentStyle = {
    flex: "1 0 auto",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    padding: isMobile ? "100px 20px 60px" : "120px 80px 80px",
    maxWidth: "1600px",
    width: "100%",
  };

  return (
    <div style={pageStyle}>
      <Navbar />
      <div style={contentStyle}>
        {/* Stepper */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 6, mx: "auto" }}>
          {STEPS.map((step, index) => (
            <Box key={step.key} sx={{ display: "flex", alignItems: "center", flex: 1 }}>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: isMobile ? "20px" : "30px",
                    height: isMobile ? "20px" : "30px",
                    borderRadius: "50%",
                    backgroundColor: index <= CURRENT_STEP_INDEX ? "#31BACD" : "transparent",
                    border: index <= CURRENT_STEP_INDEX ? "3px solid #fff" : "2px solid #D1D1D1",
                    boxShadow: index <= CURRENT_STEP_INDEX ? "0 0 0 2px #31BACD" : "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: isMobile ? "10px" : "13px",
                    fontWeight: index === CURRENT_STEP_INDEX ? "600" : "400",
                    color: index === CURRENT_STEP_INDEX ? "#000" : "#666",
                    textAlign: "center",
                    whiteSpace: "wrap",
                    width: isMobile ? "50px" : "100px",
                  }}
                >
                  {t(step.labelKey)}
                </Typography>
              </Box>
              {index < STEPS.length - 1 && (
                <Box
                  sx={{
                    height: "1px",
                    width: isMobile ? "30px" : "200px",
                    backgroundColor: "#D1D1D1",
                    mx: "auto",
                    mb: isMobile ? "18px" : "24px",
                  }}
                />
              )}
            </Box>
          ))}
        </Box>

        <Box sx={{ width: isMobile ? "100%" : "60%", mx: "auto", display: "flex", flexDirection: "column" }}>
          {/* Heading */}
          <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#000", mb: 4 }}>
            {t('RoomInfoAddStep2Title')}
          </Typography>

          <Box sx={{ mb: bookingTermsFile || bookingTermsError ? 4 : 0 }}>
            <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 1.5 }}>
              {t('RequireBookingTermsLabel')}
            </Typography>
            <RadioGroup
              row
              value={requireBookingTerms}
              onChange={(e) => {
                setRequireBookingTerms(e.target.value);
                if (e.target.value === "no") {
                  setBookingTermsFile(null);
                  setBookingTermsError(false);
                }
              }}
            >
              <FormControlLabel value="yes" control={<Radio sx={radioSx} />} label={t('Yes')} />
              <FormControlLabel value="no" control={<Radio sx={radioSx} />} label={t('No')} />
            </RadioGroup>
          </Box>

          {requireBookingTerms === "yes" && (
            <Box>
              <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 0.5 }}>
                {t('UploadBookingTermsLabel')}
              </Typography>
              <Typography sx={{ fontSize: isMobile ? "12px" : "14px", color: "#666", mb: 2 }}>
                {t('SupportedFormatsPdf')}
              </Typography>

              {!bookingTermsFile && !bookingTermsError && (
                <Box component="label" sx={uploadBoxStyle}>
                  <Box
                    sx={{
                      width: isMobile ? "36px" : "44px",
                      height: isMobile ? "36px" : "44px",
                      borderRadius: "50%",
                      border: "1.5px solid #D1D1D1",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Upload size={18} color="#666" />
                  </Box>
                  <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000" }}>
                    {t('BookingTermsLabel')}
                  </Typography>
                  <input
                    ref={bookingTermsInputRef}
                    type="file"
                    accept=".pdf"
                    style={{ display: "none" }}
                    onChange={(e) => handleFileSelect(e.target.files?.[0] || null)}
                  />
                </Box>
              )}

              {bookingTermsError && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      backgroundColor: "#FFFFFF",
                      border: "1.5px solid #F44336",
                      borderRadius: "10px",
                      padding: isMobile ? "16px" : "20px 24px",
                      width: "100%",
                      boxSizing: "border-box",
                    }}
                  >
                    <XCircle size={20} color="#F44336" />
                    <Typography sx={{ fontSize: isMobile ? "13px" : "14px", color: "#F44336" }}>
                      {t('UploadFailedError')}
                    </Typography>
                  </Box>
                  <Button
                    component="label"
                    sx={{
                      alignSelf: "flex-start",
                      color: "#31BACD",
                      textTransform: "none",
                      fontSize: isMobile ? "13px" : "14px",
                      textDecoration: "underline",
                      minWidth: "auto",
                      padding: 0,
                    }}
                  >
                    {t('UploadAgain')}
                    <input
                      type="file"
                      accept=".pdf"
                      style={{ display: "none" }}
                      onChange={(e) => handleFileSelect(e.target.files?.[0] || null)}
                    />
                  </Button>
                </Box>
              )}

              {bookingTermsFile && (
                <Box sx={fileCardStyle} onClick={() => setShowPreview(true)}>
                  <Box
                    sx={{
                      width: isMobile ? "40px" : "48px",
                      height: isMobile ? "40px" : "48px",
                      borderRadius: "6px",
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#F5F5F5",
                      border: "1px solid #E0E0E0",
                    }}
                  >
                    <FileText size={22} color="#999" />
                  </Box>
                  <Typography sx={{ flex: 1, fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000" }}>
                    {bookingTermsFile.name}
                  </Typography>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      setBookingTermsFile(null);
                    }}
                    sx={{ minWidth: "auto", padding: "8px", color: "#666" }}
                  >
                    <Trash2 size={18} />
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {/* Bottom actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              mt: 6,
            }}
          >
            <Button
              onClick={() => navigate("/hotel/onboarding")}
              sx={{
                color: "#666",
                textTransform: "none",
                fontSize: isMobile ? "14px" : "16px",
                textDecoration: "underline",
              }}
            >
              {t('SaveAndLeave')}
            </Button>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate("/hotel/add/room-info-add-1")}
                sx={{
                  color: "#666",
                  borderColor: "#D1D1D1",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { borderColor: "#999", backgroundColor: "transparent" },
                }}
              >
                {t('PreviousStep')}
              </Button>
              <Button
                variant="contained"
                disabled={isNextDisabled}
                onClick={() => navigate("/hotel/add/payment-1")}
                sx={{
                  backgroundColor: "#31BACD",
                  color: "#fff",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                  "&:disabled": { opacity: 0.5, color: "#fff" },
                }}
              >
                {t('NextStep')}
              </Button>
            </Box>
          </Box>
        </Box>
      </div>
      <Footer />

      {showPreview && bookingTermsFile && (
        <Box
          onClick={() => setShowPreview(false)}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: isMobile ? "20px" : "40px",
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              backgroundColor: "#fff",
              borderRadius: "10px",
              width: "100%",
              maxWidth: "800px",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 16px",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <Typography sx={{ fontWeight: "600", fontSize: "14px", color: "#000" }}>
                {bookingTermsFile.name}
              </Typography>
              <Button
                onClick={() => setShowPreview(false)}
                sx={{ minWidth: "auto", padding: "6px", color: "#666" }}
              >
                <X size={20} />
              </Button>
            </Box>
            <Box sx={{ flex: 1, overflow: "auto", backgroundColor: "#F5F5F5" }}>
              <Box
                component="iframe"
                src={bookingTermsPreviewUrl}
                sx={{ width: "100%", height: isMobile ? "70vh" : "80vh", border: "none" }}
              />
            </Box>
          </Box>
        </Box>
      )}
    </div>
  );
};

export default HotelAdd_RoomInfoAdd_2;
